import React, { forwardRef, useImperativeHandle, useState } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { axiosPost } from "../apis/useAxios";
import { meetsPolicy, usePasswordPolicy } from "../helpers/passwordPolicy";
import PasswordRuleList from "./Shared/PasswordRuleList";

const customStyles = {
  content: {
    width: "45%",
  },
};

const EMPTY = { currentPassword: "", newPassword: "", reTypePassword: "" };

/**
 * Change password (auth/change-password), opened with ref.showModal(): your own password needs the current one; with
 * showModal({ userName }) an administrator sets another user's password without it. The new password follows the
 * airline's rules; the user gets an email, and changing your own password logs you out.
 */
export const ChangePasswordComponent = forwardRef(function ChangePasswordComponent(_props, ref) {
  const policy = usePasswordPolicy();
  const [open, setOpen] = useState(false);
  const [userName, setUserName] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [show, setShow] = useState({});
  const [errors, setErrors] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const me = sessionStorage.getItem("session_userName") ?? "";
  const own = userName === me;

  useImperativeHandle(ref, () => ({
    showModal: (user) => {
      setUserName(user?.userName ?? me);
      setForm(EMPTY);
      setErrors(EMPTY);
      setShow({});
      setSaving(false);
      setOpen(true);
    },
  }));

  const close = () => setOpen(false);
  const change = (e) => {
    const { name, value } = e.target; // read now: React 16 reuses the event object
    setForm((f) => ({ ...f, [name]: value }));
  };
  const toggle = (name) => (e) => {
    e.preventDefault();
    setShow((s) => ({ ...s, [name]: !s[name] }));
  };

  const validate = () => {
    const next = {
      currentPassword: own && form.currentPassword === "" ? "Please enter your current password." : "",
      newPassword: form.newPassword === "" ? "Please enter new password." : "",
      reTypePassword:
        form.reTypePassword === ""
          ? "Please enter re-type password."
          : form.newPassword !== form.reTypePassword
            ? "Password does not match."
            : own && form.currentPassword === form.newPassword
              ? "Old and new password must not be same."
              : "",
    };
    setErrors(next);
    return !next.currentPassword && !next.newPassword && !next.reTypePassword;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const data = await axiosPost("auth/change-password", {
        UserName: userName,
        CurrentPassword: own ? form.currentPassword : "",
        NewPassword: form.newPassword,
        ActionUserName: me,
      });
      await Swal.fire({
        icon: data?.isSuccess ? "success" : "error",
        title: data?.isSuccess ? "Success" : "Oops...",
        text: data?.message || "Unable to change the password.",
        confirmButtonText: data?.isSuccess ? "Done" : "OK",
      });
      if (data?.isSuccess) {
        close();
        if (own) {
          // Your own password changed: log in again with it.
          sessionStorage.clear();
          window.location.href = "/";
        }
        return;
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Oops...", text: err?.message || "Unable to change the password." });
    }
    setSaving(false);
  };

  const passwordField = (name, label, placeholder) => (
    <div className="col-12">
      <div className="form-group">
        <label htmlFor={name}>
          {label} <span className="text-red">*</span>
        </label>
        <label className="mb-1 float-right">
          <a href="#" onClick={toggle(name)}>
            {show[name] ? "Hide Password" : "Show Password"}
          </a>
        </label>
        <input
          type={show[name] ? "text" : "password"}
          className="form-control form-control-sm"
          id={name}
          name={name}
          autoComplete={name === "currentPassword" ? "current-password" : "new-password"}
          placeholder={placeholder}
          maxLength={policy.maxLength}
          value={form[name]}
          onChange={change}
        />
        {errors[name] && <span className="error invalid-feedback d-block">{errors[name]}</span>}
      </div>
    </div>
  );

  return (
    <Modal
      ariaHideApp={false}
      isOpen={open}
      style={customStyles}
      className="react-modal"
      shouldCloseOnOverlayClick={false}
      onRequestClose={close}
      contentLabel="Change Password"
    >
      <form method="post" onSubmit={submit} style={saving ? { pointerEvents: "none", opacity: "0.4" } : {}}>
        <div className="modal-header">
          <h6 className="modal-title">Change Password</h6>
          <button type="button" className="close" aria-label="Close" onClick={close}>
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="modal-body">
          <p className="text-center">
            <small>
              You are going to change the password for{" "}
              <i>
                <strong className="text-danger">{userName}</strong>
              </i>{" "}
              as user name.
            </small>
          </p>
          <div className="row">
            {own && passwordField("currentPassword", "Current Password", "Enter current password")}
            {passwordField("newPassword", "New Password", "Enter new password")}
            {passwordField("reTypePassword", "Re-type New Password", "Re enter new password")}
          </div>
          <PasswordRuleList policy={policy} password={form.newPassword} />
        </div>
        <div className="modal-footer text-center">
          <button type="submit" className="btn btn-primary btn-sm" disabled={!meetsPolicy(policy, form.newPassword)} title="Change Password">
            <i className="fas fa-sign-in-alt" />
            &nbsp;Change Password
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={close} title="Close">
            <i className="fas fa-times" />
            &nbsp;Close
          </button>
        </div>
      </form>
    </Modal>
  );
});

export default ChangePasswordComponent;
